import { subscriptionsApi } from '@features/subscriptions/store/api';
import { baseApi } from '@store/baseApi';
import {
  Form,
  FormDetail,
  ExtendedFormDefinition,
  GetFormsRequest,
  GetFormsResponse,
  GetFormDetailRequest,
  GetFormDetailResponse,
  GetAvailableFormsRequest,
  GetAvailableFormsResponse,
  AssignFormsRequest,
  AssignFormsResponse,
  DeleteFormRequest,
  DeleteFormResponse,
} from '../types';

/**
 * Feature-specific tag types for forms cache management
 */
const FORMS_TAG_TYPES = ['Form', 'FormDetail', 'AvailableForm', 'PatientForms'] as const;

export const patientFormsTags = (patientId: string) => [
  { type: 'PatientForms' as const, id: patientId },
];

/**
 * Forms API - RTK Query wrapper for legacy v1 API endpoints
 * These endpoints are currently on the legacy API but will be wrapped
 * in RTK Query for better caching, optimistic updates, and state management
 */
export const formsApi = baseApi.enhanceEndpoints({ addTagTypes: FORMS_TAG_TYPES }).injectEndpoints({
  endpoints: builder => ({
    // Get forms for a specific patient
    getPatientForms: builder.query<GetFormsResponse, GetFormsRequest>({
      query: ({ patient_id, skip = 0, limit = 5, status, exclude_completed }) => {
        let queryParams = `patient_id=${patient_id}&skip=${skip}&limit=${limit}`;

        if (status === 'completed') {
          queryParams += '&status=completed';
        } else if (exclude_completed) {
          queryParams += '&exclude_completed=true';
        }

        return {
          url: `/patient/forms?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { patient_id }) =>
        result
          ? [
              ...result.forms.map(({ form_session_id }) => ({
                type: 'Form' as const,
                id: form_session_id,
              })),
              { type: 'PatientForms' as const, id: patient_id },
            ]
          : [{ type: 'PatientForms', id: patient_id }],
    }),

    // Get form detail by session ID
    getFormDetail: builder.query<GetFormDetailResponse, GetFormDetailRequest>({
      query: ({ form_session_id }) => ({
        url: `/patient/formDetail?form_session_id=${form_session_id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { form_session_id }) => [
        { type: 'FormDetail', id: form_session_id },
      ],
    }),

    // Get list of available forms (for assignment)
    getAvailableForms: builder.query<GetAvailableFormsResponse, GetAvailableFormsRequest>({
      query: ({ skip = 0, limit = 10, search }) => {
        let queryParams = `skip=${skip}&limit=${limit}`;
        if (search) {
          queryParams += `&search=${encodeURIComponent(search)}`;
        }

        return {
          url: `/forms/list?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [
              ...result.forms.map(({ form_id }) => ({
                type: 'AvailableForm' as const,
                id: form_id,
              })),
              { type: 'AvailableForm' as const, id: 'LIST' },
            ]
          : [{ type: 'AvailableForm', id: 'LIST' }],
    }),

    // Assign forms to a patient
    assignFormsToPatient: builder.mutation<AssignFormsResponse, AssignFormsRequest>({
      query: ({ patient_id, form_ids, channels }) => ({
        url: '/patient/assignForms',
        method: 'POST',
        body: {
          patient_id,
          form_ids,
          form_api_version: 'v2',
          ...(channels && { channels }),
        },
      }),
      invalidatesTags: (result, error, { patient_id }) => patientFormsTags(patient_id),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate Entitlements to refresh entitlement usage
          dispatch(subscriptionsApi.util.invalidateTags(['Entitlements']));
        } catch {
          // Error handled by mutation hook
        }
      },
    }),

    // Delete a form session
    deleteForm: builder.mutation<DeleteFormResponse, DeleteFormRequest>({
      query: ({ form_session_id }) => ({
        url: `/forms/${form_session_id}/delete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { form_session_id }) => [
        { type: 'Form', id: form_session_id },
        { type: 'FormDetail', id: form_session_id },
        // Note: We can't know the patient_id from form_session_id alone in this mutation,
        // so we invalidate all PatientForms. This could be optimized by including patient_id in the request.
        { type: 'PatientForms', id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

// Export the hooks with descriptive names
export const {
  useGetPatientFormsQuery,
  useGetFormDetailQuery,
  useGetAvailableFormsQuery,
  useAssignFormsToPatientMutation,
  useDeleteFormMutation,
} = formsApi;
